import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader } from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '../ui/table';
import { Alert, AlertTitle, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import {
  AlertCircle,
  Target,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  XCircle,
  Check,
  Minus,
} from 'lucide-react';
import productService from '../../services/productService';

const COLLECTION_DISPLAY_NAMES = {
  sales_orders: '销售订单',
  warehouse_movements: '仓库记录',
  quotations: '报价单',
  attachments: '附件',
  bill_of_materials: 'BOM',
  production_orders: '生产订单',
  purchase_orders: '采购订单',
  so_po_mappings: '销售-采购映射',
  production_mappings: '生产映射',
  inventory_movements: '库存变动',
  inventory_updates: '库存更新',
};

const ProductMergeDialog = ({ open, onClose }) => {
  const [phase, setPhase] = useState('input');
  const [productAOid, setProductAOid] = useState('');
  const [productBOid, setProductBOid] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [previewToken, setPreviewToken] = useState('');
  const [confirmInput, setConfirmInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [executeResult, setExecuteResult] = useState(null);

  const handleClose = () => {
    setPhase('input');
    setProductAOid('');
    setProductBOid('');
    setPreviewData(null);
    setPreviewToken('');
    setConfirmInput('');
    setLoading(false);
    setError(null);
    setExecuteResult(null);
    onClose();
  };

  const handlePreview = async () => {
    if (!productAOid.trim() || !productBOid.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const data = await productService.mergePreview(productAOid.trim(), productBOid.trim());
      if (data.validation_errors && data.validation_errors.length > 0) {
        setError(data.validation_errors.join('; '));
        setLoading(false);
        return;
      }
      const referencesArray = data.references
        ? Object.entries(data.references).map(([collection, count]) => ({
            collection,
            count,
          }))
        : [];

      setPreviewData({
        product_a: data.product_a || data.source_product,
        product_b: data.product_b || data.target_product,
        references: referencesArray,
        total_refs: referencesArray.reduce((sum, r) => sum + (r.count || 0), 0),
        collection_count: referencesArray.length,
      });
      setPreviewToken(data.preview_token);
      setPhase('preview');
    } catch (err) {
      setError(err.response?.data?.message || err.message || '预览失败，请检查输入');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    setPhase('confirm');
    setConfirmInput('');
  };

  const handleExecute = async () => {
    setLoading(true);
    setError(null);
    setPhase('executing');
    try {
      const data = await productService.mergeExecute(
        productAOid.trim(),
        productBOid.trim(),
        previewToken
      );
      setExecuteResult(data);
      setPhase('result');
    } catch (err) {
      setError(err.response?.data?.message || err.message || '执行合并失败');
      setPhase('result');
    } finally {
      setLoading(false);
    }
  };

  const productA = previewData?.product_a;
  const productB = previewData?.product_b;
  const references = previewData?.references || [];
  const totalRefs = previewData?.total_refs || 0;
  const collectionCount = previewData?.collection_count || 0;

  const renderInputPhase = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-destructive" />
          产品 A（将被合并删除）
        </Label>
        <Input
          placeholder="请输入产品 ObjectId"
          value={productAOid}
          onChange={(e) => setProductAOid(e.target.value)}
          disabled={loading}
        />
        <p className="text-xs text-muted-foreground">支持直接粘贴 24 位 ObjectId 字符串</p>
      </div>

      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Target className="h-4 w-4 text-success" />
          产品 B（合并目标产品）
        </Label>
        <Input
          placeholder="请输入产品 ObjectId"
          value={productBOid}
          onChange={(e) => setProductBOid(e.target.value)}
          disabled={loading}
        />
      </div>

      <Alert variant="warning">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          产品 A 合并后将永久删除，此操作不可撤销
        </AlertDescription>
      </Alert>

      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );

  const renderPreviewPhase = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-l-4 border-l-destructive">
          <CardHeader>
            <Badge variant="destructive">产品 A（将被删除）</Badge>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div><span className="text-muted-foreground">货号：</span><span className="font-medium">{productA?.product_id || '-'}</span></div>
            <div><span className="text-muted-foreground">名称：</span><span className="font-medium">{productA?.name || '-'}</span></div>
            <div><span className="text-muted-foreground">规格：</span><span className="font-medium">{productA?.specification || '-'}</span></div>
            <div><span className="text-muted-foreground">分类：</span><span className="font-medium">{productA?.category || '-'}</span></div>
            <div><span className="text-muted-foreground">类型：</span><span className="font-medium">{productA?.product_type || '-'}</span></div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-success">
          <CardHeader>
            <Badge variant="success">产品 B（保留目标）</Badge>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div><span className="text-muted-foreground">货号：</span><span className="font-medium">{productB?.product_id || '-'}</span></div>
            <div><span className="text-muted-foreground">名称：</span><span className="font-medium">{productB?.name || '-'}</span></div>
            <div><span className="text-muted-foreground">规格：</span><span className="font-medium">{productB?.specification || '-'}</span></div>
            <div><span className="text-muted-foreground">分类：</span><span className="font-medium">{productB?.category || '-'}</span></div>
            <div><span className="text-muted-foreground">类型：</span><span className="font-medium">{productB?.product_type || '-'}</span></div>
          </CardContent>
        </Card>
      </div>

      <Separator />

      <div>
        <h4 className="text-sm font-medium mb-2">📊 将受影响的关联数据</h4>
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>集合</TableHead>
                  <TableHead className="text-right">引用数量</TableHead>
                  <TableHead>说明</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {references.map((ref, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{COLLECTION_DISPLAY_NAMES[ref.collection] || ref.collection}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="secondary">{ref.count_a || ref.count || 0}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      产品 A 在 {ref.count_a || ref.count || 0} 条{COLLECTION_DISPLAY_NAMES[ref.collection] || ref.collection}中
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell className="font-bold">合计</TableCell>
                  <TableCell className="text-right font-bold">{totalRefs}</TableCell>
                  <TableCell className="text-muted-foreground">共 {collectionCount} 个集合受影响</TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderConfirmPhase = () => (
    <div className="space-y-4">
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>此操作不可撤销！</AlertTitle>
        <AlertDescription>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>产品「{productA?.name || productA?.product_id}」({productA?.product_id}) 的所有关联将被迁移到产品「{productB?.name || productB?.product_id}」</li>
            <li>涉及 {collectionCount} 个集合共 {totalRefs} 条关联数据</li>
            <li>产品「{productA?.name || productA?.product_id}」将被永久删除</li>
          </ul>
        </AlertDescription>
      </Alert>

      <div className="space-y-2">
        <Label>请输入产品 A 的货号 "{productA?.product_id}" 以确认</Label>
        <Input
          placeholder={productA?.product_id || '输入货号确认'}
          value={confirmInput}
          onChange={(e) => setConfirmInput(e.target.value)}
        />
      </div>
    </div>
  );

  const renderExecutingPhase = () => (
    <div className="flex flex-col items-center justify-center py-12 gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-muted-foreground">正在执行合并操作...</p>
    </div>
  );

  const renderResultPhase = () => {
    if (error) {
      return (
        <div className="space-y-4">
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertTitle>合并失败</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      );
    }

    const result = executeResult;
    if (!result) return null;

    const migratedCounts = result.migrated_counts || {};
    const isPartial = Object.values(migratedCounts).some(v => v === -1);
    const isSuccess = result.success !== false && !isPartial;

    return (
      <div className="space-y-4">
        <Alert variant={isSuccess ? 'success' : 'warning'}>
          {isSuccess ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          <AlertTitle>{isSuccess ? '合并完成！' : '部分失败'}</AlertTitle>
          {!isSuccess && (
            <AlertDescription>部分集合迁移失败，请查看详情</AlertDescription>
          )}
        </Alert>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>集合</TableHead>
                  <TableHead className="text-right">数量</TableHead>
                  <TableHead>状态</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(migratedCounts).map(([col, count]) => (
                  <TableRow key={col}>
                    <TableCell>{COLLECTION_DISPLAY_NAMES[col] || col}</TableCell>
                    <TableCell className="text-right">{count >= 0 ? count : '-'}</TableCell>
                    <TableCell>
                      {count > 0 ? (
                        <Badge variant="success" className="gap-1"><Check className="h-3 w-3" />成功</Badge>
                      ) : count === 0 ? (
                        <Badge variant="secondary" className="gap-1"><Minus className="h-3 w-3" />跳过</Badge>
                      ) : (
                        <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />失败</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <div className="text-sm">
          {result.deleted ? (
            <span className="flex items-center gap-1 text-success">
              <CheckCircle2 className="h-4 w-4" /> 「{productA?.name || productA?.product_id}」已删除
            </span>
          ) : (
            <span className="flex items-center gap-1 text-destructive">
              <XCircle className="h-4 w-4" /> 产品 A 未被删除
            </span>
          )}
        </div>

        {result.operation_log_id && (
          <div className="text-xs text-muted-foreground">
            操作日志: <code className="bg-muted px-2 py-1 rounded">{result.operation_log_id}</code>
          </div>
        )}
      </div>
    );
  };

  const getFooterButtons = () => {
    switch (phase) {
      case 'input':
        return (
          <>
            <Button variant="outline" onClick={handleClose} disabled={loading}>取消</Button>
            <Button onClick={handlePreview} disabled={!productAOid.trim() || !productBOid.trim() || loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              预览合并 →
            </Button>
          </>
        );
      case 'preview':
        return (
          <>
            <Button variant="outline" onClick={() => { setPhase('input'); setError(null); }}>← 返回修改</Button>
            <Button variant="destructive" onClick={handleConfirm}>确认合并 →</Button>
          </>
        );
      case 'confirm':
        return (
          <>
            <Button variant="outline" onClick={() => setPhase('preview')}>← 返回</Button>
            <Button
              variant="destructive"
              onClick={handleExecute}
              disabled={confirmInput !== (productA?.product_id || '')}
            >
              ⚠ 确认执行合并
            </Button>
          </>
        );
      case 'executing':
        return <Button variant="outline" disabled>执行中...</Button>;
      case 'result':
        return <Button onClick={handleClose}>关闭</Button>;
      default:
        return null;
    }
  };

  const getDialogTitle = () => {
    switch (phase) {
      case 'input': return '产品合并';
      case 'preview': return '产品合并 - 预览';
      case 'confirm': return '产品合并 - 最终确认';
      case 'executing': return '产品合并 - 执行中';
      case 'result': return error ? '产品合并 - 失败' : '产品合并 - 完成';
      default: return '产品合并';
    }
  };

  const getDialogDescription = () => {
    switch (phase) {
      case 'input': return '将产品 A 的所有关联数据迁移到产品 B，并删除产品 A';
      case 'preview': return '确认以下产品信息和影响的关联数据';
      case 'confirm': return '请仔细确认以下信息';
      default: return '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && phase !== 'executing') handleClose(); }}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-y-auto"
        onPointerDownOutside={(e) => { if (phase === 'executing') e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
          <DialogDescription>{getDialogDescription()}</DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {phase === 'input' && renderInputPhase()}
          {phase === 'preview' && renderPreviewPhase()}
          {phase === 'confirm' && renderConfirmPhase()}
          {phase === 'executing' && renderExecutingPhase()}
          {phase === 'result' && renderResultPhase()}
        </div>

        <DialogFooter>
          {getFooterButtons()}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProductMergeDialog;